import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Reveal, Overline } from "./Reveal";
import { api } from "../lib/api";

export const PageHero = ({ overline, title, subtitle, image }) => (
  <section className="relative bg-[#0B1F3A] text-white overflow-hidden">
    <div className="absolute inset-0">
      {image && <img src={image} alt="" className="w-full h-full object-cover opacity-25" />}
      <div className="absolute inset-0 bg-[#0B1F3A]/70" />
      <div className="absolute inset-0 tech-grid-dark opacity-40" />
    </div>
    <div className="grain" />
    <div className="relative max-w-[1360px] mx-auto px-5 md:px-8 pt-24 pb-20 md:pt-28 md:pb-24">
      <Reveal><Overline dark>{overline}</Overline></Reveal>
      <Reveal delay={0.1}>
        <h1 className="mt-5 font-head font-extrabold text-4xl md:text-6xl tracking-tight max-w-4xl leading-[1.05]">{title}</h1>
      </Reveal>
      {subtitle && <Reveal delay={0.2}><p className="mt-6 text-lg text-[#CBD5E1] max-w-2xl leading-relaxed">{subtitle}</p></Reveal>}
    </div>
  </section>
);

export const SectionHeading = ({ overline, title, sub, center, dark }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
    <Overline dark={dark}>{overline}</Overline>
    <h2 className={`mt-4 font-head font-bold text-3xl md:text-5xl tracking-tight ${dark ? "text-white" : "text-[#0B1F3A]"}`}>{title}</h2>
    {sub && <p className={`mt-5 text-lg leading-relaxed ${dark ? "text-[#CBD5E1]" : "text-[#64748B]"}`}>{sub}</p>}
  </div>
);

export const CTABand = () => (
  <section className="relative bg-[#0B1F3A] text-white overflow-hidden" data-testid="cta-band">
    <div className="absolute inset-0 tech-grid-dark opacity-40" />
    <div className="grain" />
    <div className="relative max-w-[1360px] mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
      <Reveal>
        <h2 className="font-head font-extrabold text-3xl md:text-5xl tracking-tight max-w-3xl mx-auto leading-tight">Looking for a Reliable Telecom Infrastructure Partner?</h2>
        <p className="mt-6 text-lg text-[#CBD5E1] max-w-2xl mx-auto">Speak with Airanet about network rollout, maintenance, infrastructure engineering or technical resource requirements.</p>
        <div className="mt-9 flex flex-wrap gap-4 justify-center">
          <Link to="/contact" data-testid="cta-request" className="inline-flex items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold px-7 py-3.5 rounded-md hover:-translate-y-0.5 transition-transform">Request a Capability Discussion</Link>
          <Link to="/contact" data-testid="cta-contact" className="inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 rounded-md hover:bg-white/10">Contact Our Team</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const ServiceCard = ({ s, index }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block h-full bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover-lift hover:border-[#1EA7E1] hover:shadow-xl">
      <div className="relative h-44 overflow-hidden clip-corner">
        <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 to-transparent" />
        <span className="absolute top-3 left-3 font-mono2 text-xs text-white/80">{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="p-6">
        <h3 className="font-head font-semibold text-xl text-[#0B1F3A] group-hover:text-[#1261A0]">{s.title}</h3>
        <p className="mt-3 text-[#64748B] leading-relaxed text-[15px]">{s.short}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1261A0]">Explore service <span className="transition-transform group-hover:translate-x-1">→</span></span>
      </div>
    </Link>
  </Reveal>
);


export const Testimonials = ({ dark = false }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/testimonials")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  }, []);
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <section className={`py-24 md:py-28 ${dark ? "bg-[#0B1F3A] text-white relative overflow-hidden" : "bg-[#F4F6F8]"}`} data-testid="testimonials-section">
      {dark && <div className="absolute inset-0 tech-grid-dark opacity-40" />}
      <div className="relative max-w-[1360px] mx-auto px-5 md:px-8">
        <SectionHeading dark={dark} overline="Client Voices" title="What Our Clients Say" center />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((t, i) => (
            <Reveal key={t.id} delay={(i % 3) * 0.07}>
              <figure className={`h-full rounded-lg p-7 border ${dark ? "bg-white/5 border-white/10" : "bg-white border-[#E2E8F0]"}`}>
                <span className="font-head font-extrabold text-4xl text-[#F28C28] leading-none">&ldquo;</span>
                <blockquote className={`mt-2 leading-relaxed ${dark ? "text-[#CBD5E1]" : "text-[#252B33]"}`}>{t.quote}</blockquote>
                <figcaption className="mt-5">
                  <p className={`font-head font-semibold ${dark ? "text-white" : "text-[#0B1F3A]"}`}>{t.author}</p>
                  {t.company && <p className="text-sm text-[#64748B]">{t.company}</p>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ClientsStrip = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  }, []);
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <section className="py-16 bg-white border-y border-[#E2E8F0]" data-testid="clients-strip">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8">
        <p className="text-center text-xs uppercase tracking-[0.22em] text-[#64748B] font-semibold mb-8">Trusted by Teams We Support</p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
          {list.map((c) => (
            <div key={c.id} className="opacity-70 hover:opacity-100 transition-opacity" title={c.name}>
              {c.logo ? <img src={c.logo} alt={c.name} loading="lazy" className="h-9 md:h-10 object-contain grayscale hover:grayscale-0 transition-all" /> : <span className="font-head font-semibold text-[#0B1F3A]">{c.name}</span>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
